import { useEffect, useState } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'

export const useOrganizations = ({ ids, typeValidations }) => {
  const [organizations, setOrganizations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState()

  useEffect(() => {
    let isMounted = true

    async function fetchOrganizations() {
      if (!ids || ids.length === 0) {
        if (isMounted) {
          setLoading(false)
          setError('No organization IDs provided')
        }
        return
      }

      try {
        if (isMounted) {
          setLoading(true)
          setError()
        }

        let data, fetchError
        try {
          const result = await supabaseGetter.organizations.getByIds(ids)
          data = result.data
          fetchError = result.error
        } catch (err) {
          // Supabase throws "callback is no longer runnable" when component unmounts
          // during query execution - this is safe to ignore
          if (!isMounted && err.message?.includes('no longer runnable')) {
            console.log('[use-organizations] Suppressed unmount error:', err.message)
            return
          }
          throw err
        }

        if (!isMounted) return

        if (fetchError) {
          console.warn('Error fetching organizations:', fetchError)
          setError(fetchError.message || 'Failed to fetch organizations')
          setOrganizations([])
          return
        }

        if (!data) {
          setError('Organizations not found')
          setOrganizations([])
          return
        }

        // Reorder to match ids input order — .in() doesn't preserve order
        const orderedData = ids.map(id => data.find(org => org.id === id)).filter(Boolean)

        if (typeValidations) {
          let validationError = null
          orderedData.forEach((organization, index) => {
            if (organization.organization_type !== typeValidations[index]) {
              validationError = `Organization ${organization.id} is not a ${typeValidations[index]}`
            }
          })

          if (validationError) {
            setError(validationError)
            setOrganizations([])
            return
          }
        }

        setOrganizations(orderedData)
      } catch (err) {
        if (!isMounted) return

        console.warn('Error in fetchOrganizations:', err)
        setError('An unexpected error occurred')
        setOrganizations([])
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchOrganizations()

    return () => {
      isMounted = false
    }
  }, [ids, typeValidations])

  return {
    data: organizations,
    loading,
    error,
    invalidate: supabaseGetter.organizations.invalidate
  }
}
